// src/components/admin/AdminHeader.tsx
import React, { useState } from 'react';
import { Box, IconButton, Avatar, Typography, Menu, MenuItem, Divider, ListItemIcon } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import SearchIcon from '@mui/icons-material/Search';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate } from 'react-router-dom';

interface AdminHeaderProps {
  onToggleMenu: () => void;
}

// Normalize role for color coding (same as in SlideMenu)
const normalizeRole = (role: string): string => {
  const r = role?.toLowerCase().trim() || '';
  if (!r || r === 'new' || r === 'unknown') return 'admin';
  if (r.includes('super')) return 'super_admin';
  if (r === 'operation' || r === 'vendor') return r;
  return 'admin';
};

// Map normalized role to color
const getRoleColor = (normalizedRole: string): string => {
  switch (normalizedRole) {
    case 'super_admin': return '#1565c0'; // Blue
    case 'admin':       return '#6a1b9a'; // Purple
    case 'operation':   return '#e65100'; // Orange
    case 'vendor':      return '#4caf50'; // Green
    default:            return '#008060'; // Teal (fallback)
  }
};

const AdminHeader: React.FC<AdminHeaderProps> = ({ onToggleMenu }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const open = Boolean(anchorEl);

  // --- Get user info from localStorage (keys set in adminLogin.tsx) ---
  const userName  = localStorage.getItem('admin_name')  || 'Admin';
  const userEmail = localStorage.getItem('admin_email') || 'admin@example.com';

  // Raw role from API (preserved under 'role')
  const rawRole = localStorage.getItem('role') || 'admin';

  // Normalized role for color coding
  const normalizedRole = normalizeRole(rawRole);

  // Avatar letter
  const avatarLetter = userName.charAt(0).toUpperCase() || userEmail.charAt(0).toUpperCase();

  // Format raw role for display (capitalize first letter)
  const displayRole = rawRole
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    handleClose();
    navigate('/adminProfile');
  };

  const handleSettings = () => {
    handleClose();
    navigate('/globalSettings');
  };

  const handleLogout = () => {
    // Clear all admin related localStorage keys
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    localStorage.removeItem('admin_name');
    localStorage.removeItem('admin_type');
    localStorage.removeItem('role');
    localStorage.removeItem('admin_role_id');
    localStorage.removeItem('admin_permissions');
    localStorage.removeItem('admin_perm_map');
    // Also clear any old keys
    localStorage.removeItem('user_email');
    localStorage.removeItem('user_name');
    localStorage.removeItem('user_role');

    handleClose();
    navigate('/adminLogin');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '71px',
        px: 2,
        backgroundColor: '#f7f7f7',
        borderBottom: '1px solid #e1e3e5',
        position: 'sticky',
        top: 0,
        zIndex: 1100,
      }}
    >
      {/* Left Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton
          onClick={onToggleMenu}
          sx={{
            display: { xs: 'flex', md: 'none' },
            color: '#202223',
            '&:hover': { backgroundColor: '#e4e5e7' },
          }}
        >
          <MenuIcon />
        </IconButton>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: '#202223',
            fontSize: '16px',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          Admin Panel
        </Typography>
      </Box>

      {/* Right Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* Role Badge - shows raw role name */}
        <Box
          sx={{
            px: 1.5,
            py: 0.5,
            backgroundColor: getRoleColor(normalizedRole),
            borderRadius: '4px',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'white',
              fontWeight: 600,
              fontSize: '11px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            {displayRole}
          </Typography>
        </Box>

        <Avatar
          onClick={handleClick}
          sx={{
            width: 32,
            height: 32,
            backgroundColor: getRoleColor(normalizedRole),
            fontSize: '14px',
            fontWeight: 600,
            ml: 1,
            cursor: 'pointer',
            '&:hover': { opacity: 0.8 },
          }}
        >
          {avatarLetter}
        </Avatar>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          onClick={handleClose}
          PaperProps={{
            elevation: 3,
            sx: {
              mt: 1.5,
              minWidth: 240,
              borderRadius: '8px',
              '& .MuiMenuItem-root': {
                px: 2,
                py: 1.5,
                fontSize: '14px',
                '&:hover': {
                  backgroundColor: '#f6f6f7',
                },
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {/* Profile Info Section */}
          <Box sx={{ px: 2, py: 1.5, cursor: 'default' }}>
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600, 
                color: '#202223',
                mb: 0.5,
              }}
            >
              {userName}
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#6d7175',
                display: 'block',
                mb: 0.5,
              }}
            >
              {userEmail}
            </Typography>
            <Box
              sx={{
                display: 'inline-block',
                px: 1,
                py: 0.25,
                backgroundColor: getRoleColor(normalizedRole),
                borderRadius: '4px',
                mt: 0.5,
              }}
            >
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '11px',
                  textTransform: 'uppercase',
                }}
              >
                {displayRole}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 1 }} />

          {/* Menu Items */}
          <MenuItem onClick={handleProfile}>
            <ListItemIcon>
              <PersonOutlineIcon fontSize="small" sx={{ color: '#202223' }} />
            </ListItemIcon>
            Your Profile
          </MenuItem>

          <MenuItem onClick={handleSettings}>
            <ListItemIcon>
              <SettingsOutlinedIcon fontSize="small" sx={{ color: '#202223' }} />
            </ListItemIcon>
            Settings
          </MenuItem>

          <Divider sx={{ my: 1 }} />

          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" sx={{ color: '#d72c0d' }} />
            </ListItemIcon>
            <Typography sx={{ color: '#d72c0d', fontWeight: 500 }}>
              Logout
            </Typography>
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export default AdminHeader;